// working fine now
import React, { useState, useEffect, useRef } from "react";
import "./DepositorForm.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNotifications } from "../Admin/NotificationContext";
import { FaFileAlt, FaDownload } from "react-icons/fa";
import PubmedIdField from "./PubmedIdField";
import { usePubmedValidator } from "./usePubmedValidator";
import { useAuth } from "../../contexts/AuthContext";
import SpeciesEvidenceSection, { parseSpecies } from "./SpeciesEvidenceSection";
import { COLLECTIONS_OPTIONS } from "../../utils/collectionsOptions";

const toTitleCase = (str) => {
  if (!str) return str;
  return String(str).toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
};
const titleCaseFields = new Set([
  "depositor_name_contributor",
  "depositor_institution",
  "host_species",
  "antigen_species",
  "positive_tested_species",
]);

const STORAGE_KEY = "dshb_entries";

// Multi-select tag dropdown for Collections, mirroring the Admin side's
// CollectionsField (ProductDetails.js) but as a simple controlled
// value/onChange component to match this form's existing style.
function CollectionsMultiSelect({ value, onChange }) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const current = Array.isArray(value) ? value : [];
  const filtered = COLLECTIONS_OPTIONS.filter(
    (o) => o.toLowerCase().includes(search.toLowerCase()) && !current.includes(o)
  );

  const add = (val) => {
    if (!val || current.includes(val)) return;
    onChange([...current, val]);
    setSearch("");
  };
  const remove = (val) => onChange(current.filter((c) => c !== val));

  return (
    <div style={{ position: "relative" }}>
      {current.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
          {current.map((c) => (
            <span key={c} style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              background: "#eef2ff", color: "#3730a3", border: "1px solid #c7d2fe",
              borderRadius: "999px", padding: "3px 10px", fontSize: "13px",
            }}>
              {c}
              <span onClick={() => remove(c)} style={{ cursor: "pointer", fontWeight: 700, color: "#818cf8" }} title="Remove">×</span>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        placeholder="Click to see all collections, or type to search…"
        value={search}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 150)}
        onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
      />
      {isOpen && (
        <div style={{
          position: "absolute", left: 0, right: 0, top: "100%", marginTop: "4px",
          background: "#fff", border: "1px solid #D4D8DF", borderRadius: "8px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 9999,
          maxHeight: "220px", overflowY: "auto",
        }}>
          {filtered.length > 0 ? (
            filtered.map((o) => (
              <div key={o}
                onClick={() => add(o)}
                onMouseDown={(e) => e.preventDefault()}
                style={{ padding: "8px 12px", fontSize: "13px", cursor: "pointer" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
              >
                {o}
              </div>
            ))
          ) : (
            <div style={{ padding: "10px 12px", fontSize: "12px", color: "#9ca3af" }}>
              {search ? "No matching collections" : "All collections already selected"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Isotype options split by clonality — Polyclonal only shows serum-type isotopes
const MONOCLONAL_ISOTOPES = [
  "Armenian Hamster IgG","Goat IgG","Guinea Pig IgG","Human IgG1",
  "Mouse IgA","Mouse IgA, kappa light chain","Mouse IgG","Mouse IgG1",
  "Mouse IgG1, kappa light chain","Mouse IgG1, lambda light chain",
  "Mouse IgG2a","Mouse IgG2a, kappa light chain","Mouse IgG2a, lambda light chain",
  "Mouse IgG2b","Mouse IgG2b, kappa light chain","Mouse IgG2c",
  "Mouse IgG2c, kappa light chain","Mouse IgG3","Mouse IgG3, kappa light chain",
  "Mouse IgG3, lambda light chain","Mouse IgM","Mouse IgM, kappa light chain",
  "Mouse IgM, lambda light chain","Rabbit IgG","Rat IgG","Rat IgG1",
  "Rat IgG1, kappa light chain","Rat IgG2a","Rat IgG2a, kappa light chain",
  "Rat IgG2a, lambda light chain","Rat IgG2b","Rat IgG2b, kappa light chain",
  "Rat IgG2c","Rat IgM","Rat IgM, kappa light chain","Syrian Hamster IgG",
];
const POLYCLONAL_ISOTOPES = [
  "Mouse IgG","Rabbit IgG","Rat IgG","Goat IgG","Guinea Pig IgG",
  "Armenian Hamster IgG","Syrian Hamster IgG",
];

// Small helper component for institution autocomplete dropdown rows.
// Needs its own state for hover so React properly re-renders on hover
// (direct style mutation via onMouseEnter doesn't trigger React re-renders).
function InstDropdownItem({ inst, onSelect }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseDown={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "10px 14px", cursor: "pointer",
        borderBottom: "1px solid #f1f5f9", fontSize: "13px",
        background: hovered ? "#E1E9FC" : "#fff",
        transition: "background 0.1s",
      }}
    >
      <strong style={{ color: "#14161B", display: "block" }}>{inst.name}</strong>
      {(inst.city || inst.country) && (
        <span style={{ fontSize: "11px", color: "#6b7280" }}>
          {[inst.city, inst.country].filter(Boolean).join(", ")}
        </span>
      )}
    </div>
  );
}

export default function DepositorForm({ onSubmitAll, entryToEdit, onEntryToEditConsumed, tourRefs, onStartTour }) {
  const { validityMap, validateIds, resetAll } = usePubmedValidator();
  const { addNotification } = useNotifications();
  const { user } = useAuth();

  const downloadTemplate = () => {
    const link = document.createElement("a");
    link.href = `${process.env.PUBLIC_URL}/DSHB_Antibody_Template.xlsx`;
    link.download = "DSHB_Antibody_Template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Template downloaded!");
  };

  const initialFormState = {
    item_name_number: "", isotype: "", antigen: "", alternative_antigen_names: "",
    pubmed_url: "", depositor_growth_medium: "", depositor_institution: "",
    depositor_email: "", depositor_website: "", depositor_name_contributor: "",
    host_species: "", antigen_species: "", positive_tested_species: "",
    alternate_name_clone_id: "", additional_information: "", additional_characterization: "",
    initial_pubmed_id: "", depositor_notes: "", protocol_file: null,
    antigen_molecular_weight: "", gene: "", alternate_gene_names: "", entrez_gene_id: "",
    uniprot_id: "", immunogen: "", immunogen_sequence: "", clonality: "",
    myeloma_strain: "", epitope_mapped: "", epitope_location_sequence: "",
    epitope_map_pubmed_id: "", ihc_pubmed_id: "", if_pubmed_ids: "", wb_pubmed_id: "",
    ip_pubmed_ids: "", chip_pubmed_ids: "", elisa_pubmed_ids: "", ffpe_pubmed_ids: "",
    fb_pubmed_ids: "", microarray_pubmed_ids: "", trap_pubmed_ids: "", facs_pubmed_ids: "",
    gel_supershift_pubmed_ids: "", other_pubmed_ids: "", applications: [],
    collections: [],
  };

  const [initial] = useState(() => {
    let entries = [];
    try { entries = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); }
    catch { entries = []; }
    return {
      entries,
      formOverride: entryToEdit || null,
      editingId: entryToEdit ? entryToEdit.id : null,
    };
  });

  const [formData, setFormData] = useState(() =>
    initial.formOverride ? { ...initialFormState, ...initial.formOverride } : initialFormState
  );
  const [fileUploading, setFileUploading]   = useState(false);
  const [uniprotValid, setUniprotValid]     = useState(null);
  const [editingEntryId, setEditingEntryId] = useState(initial.editingId);
  const [submitting, setSubmitting]         = useState(false);
  const [submitSuccess, setSubmitSuccess]   = useState(false);
  const [errors, setErrors]                 = useState({});
  const [showPubmedIds, setShowPubmedIds]   = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // ── New state for uniqueness checks and institution autocomplete ──
  const [itemNameStatus, setItemNameStatus] = useState(null);
  const [cloneIdStatus, setCloneIdStatus]   = useState(null);
  const [institutionSuggestions, setInstitutionSuggestions] = useState([]);
  const [showInstDropdown, setShowInstDropdown] = useState(false);
  const [instManualEntry, setInstManualEntry] = useState(false);
  const instSearchTimeout = useRef(null);
  // Species reactivity evidence — one entry per species in positive_tested_species
  const [speciesEvidence, setSpeciesEvidence] = useState([]);

  const [entries, setEntries] = useState(initial.entries);
  const draftEntries = entries.filter(e => e.status === "draft");

  const handleAutoResize = (e) => {
    e.target.style.height = "auto";
    e.target.style.height = e.target.scrollHeight + "px";
  };

  const fileInputRef         = useRef(null);
  const protocolFileInputRef = useRef(null);
  const tableRef             = useRef(null);
  const formTopRef           = useRef(null);
  const formRef              = useRef(null);

  const [attentionExpanded, setAttentionExpanded] = useState(false);

  // Welcome tour: the tour itself (state, step list, the overlay UI) lives
  // in DepositorDashboard, since several steps point at things outside this
  // component entirely (History, Profile, Help in the navbar). This
  // component just registers its own elements into the shared `tourRefs`
  // bucket passed down as a prop, via setTourRef("key") callback refs below.
  const setTourRef = (key) => (el) => { if (tourRefs?.current) tourRefs.current[key] = el; };

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(entries)); }
    catch { toast.error("Warning: Could not save to browser storage"); }
  }, [entries]);

  useEffect(() => {
    const fn = (e) => {
      if (draftEntries.length > 0) { e.preventDefault(); e.returnValue = "Unsaved data will be lost."; }
    };
    window.addEventListener("beforeunload", fn);
    return () => window.removeEventListener("beforeunload", fn);
  }, [draftEntries.length]);

  useEffect(() => {
    if (initial.editingId !== null) {
      toast.success("Draft loaded for editing.");
      scrollTo(formRef);
    }
    if (onEntryToEditConsumed) onEntryToEditConsumed();
  }, []);

  const scrollTo = (ref) =>
    setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);

  // ── Item name uniqueness check ────────────────────────────────────
  const checkItemName = async (name) => {
    const trimmed = name?.trim();
    if (!trimmed) { setItemNameStatus(null); return; }
    setItemNameStatus("checking");
    try {
      const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/check-item-name", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemName: trimmed, depositorName: formData.depositor_name_contributor }),
      });
      const data = await res.json();
      setItemNameStatus(data.available ? "available" : {
        taken: true,
        message: data.message,
        suggestions: data.suggestions || [],
        tip: data.tip || "",
      });
    } catch { setItemNameStatus(null); }
  };

  // ── Clone ID duplicate check ──────────────────────────────────────
  const checkCloneId = async (cloneId) => {
    const trimmed = cloneId?.trim();
    if (!trimmed) { setCloneIdStatus(null); return; }
    setCloneIdStatus("checking");
    try {
      const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/check-clone-id", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cloneId: trimmed }),
      });
      const data = await res.json();
      setCloneIdStatus(data.available ? "available" : { taken: true });
    } catch { setCloneIdStatus(null); }
  };

  // ── Institution autocomplete via ROR API (called from browser) ────
  const searchInstitutions = (query) => {
    const trimmed = query?.trim();
    if (!trimmed || trimmed.length < 3) {
      setInstitutionSuggestions([]); setShowInstDropdown(false); return;
    }
    clearTimeout(instSearchTimeout.current);
    instSearchTimeout.current = setTimeout(async () => {
      try {
        // Try ROR v2 first (current API), fall back to v1 format if needed.
        // v2 response: { items: [{ names: [{value, types}], locations: [{geonames_details:{city,country_name}}] }] }
        // v1 response: { items: [{ name: "...", country: {country_name}, addresses: [{city}] }] }
        const res = await fetch(
          `https://api.ror.org/v2/organizations?query=${encodeURIComponent(trimmed)}`,
          { headers: { "Accept": "application/json" } }
        );
        if (!res.ok) throw new Error("ROR API error");
        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];

        const results = items.slice(0, 8).map(org => {
          // v2: names is an array, get the first English or default name
          let name = "";
          if (Array.isArray(org.names)) {
            // Prefer type "ror_display", then "label", then first entry
            const display = org.names.find(n => n.types?.includes("ror_display"));
            const label   = org.names.find(n => n.types?.includes("label"));
            name = (display || label || org.names[0])?.value || "";
          } else if (typeof org.name === "string") {
            // v1 fallback
            name = org.name;
          }

          // v2: locations array
          let city = "", country = "";
          if (Array.isArray(org.locations) && org.locations[0]?.geonames_details) {
            city    = org.locations[0].geonames_details.city || "";
            country = org.locations[0].geonames_details.country_name || "";
          } else if (org.country) {
            // v1 fallback
            country = org.country.country_name || "";
            city    = Array.isArray(org.addresses) ? org.addresses[0]?.city || "" : "";
          }

          return { name, city, country };
        }).filter(r => r.name.length > 0);

        setInstitutionSuggestions(results);
        setShowInstDropdown(results.length > 0);
      } catch {
        setInstitutionSuggestions([]);
        setShowInstDropdown(false);
      }
    }, 300);
  };

  // ── Template upload ───────────────────────────────────────────────
  const handleParsedData = async (records) => {
    if (!records.length) return;

    const withId = records.map((r) => ({
      ...initialFormState, ...r, id: Date.now() + Math.random(),
      status: "draft",
      applications: Array.isArray(r.applications) ? r.applications
        : (r.applications ? String(r.applications).split(",").map(a => a.trim()).filter(Boolean) : []),
    }));

    // Same-batch duplicate check — two NEW rows in the same upload sharing
    // an Item Name/Number wouldn't be caught by the backend check below at
    // all, since neither one exists in the DB yet to conflict with; they
    // only conflict with each other. Purely client-side, no API call needed.
    const nameCounts = {};
    withId.forEach(r => {
      const key = r.item_name_number?.trim().toLowerCase();
      if (key) nameCounts[key] = (nameCounts[key] || 0) + 1;
    });
    const batchDuplicates = withId.filter(r => {
      const key = r.item_name_number?.trim().toLowerCase();
      return key && nameCounts[key] > 1;
    });
    batchDuplicates.forEach(r => { r._itemNameTaken = true; });

    // Item Name/Number ("Clone ID") duplicate check — manual entry already
    // checks this against the backend via checkItemName() on blur, but
    // bulk-uploaded rows skipped that entirely, so an uploaded row could
    // silently reuse an already-registered Clone ID. Check every row here
    // too, same endpoint/payload shape as checkItemName, and flag matches
    // (same "import anyway, but warn" pattern used above for missing
    // mandatory fields, rather than blocking the import outright).
    const toCheckItemName = withId.filter(r => r.item_name_number?.trim());
    if (toCheckItemName.length) {
      const results = await Promise.all(toCheckItemName.map(async (r) => {
        try {
          const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/check-item-name", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ itemName: r.item_name_number.trim(), depositorName: r.depositor_name_contributor }),
          });
          const data = await res.json();
          return { id: r.id, taken: !data.available };
        } catch { return { id: r.id, taken: false }; }
      }));
      const takenIds = new Set(results.filter(r => r.taken).map(r => r.id));
      withId.forEach(r => { if (takenIds.has(r.id)) r._itemNameTaken = true; });
    }

    // Alternate Name/Clone ID (a separate, optional field) — same idea,
    // its own manual-entry check via checkCloneId()/api/check-clone-id.
    const toCheckAlternate = withId.filter(r => r.alternate_name_clone_id?.trim());
    if (toCheckAlternate.length) {
      const results = await Promise.all(toCheckAlternate.map(async (r) => {
        try {
          const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/check-clone-id", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ cloneId: r.alternate_name_clone_id.trim() }),
          });
          const data = await res.json();
          return { id: r.id, taken: !data.available };
        } catch { return { id: r.id, taken: false }; }
      }));
      const takenIds = new Set(results.filter(r => r.taken).map(r => r.id));
      withId.forEach(r => { if (takenIds.has(r.id)) r._cloneIdTaken = true; });
    }

    // Positive Tested Species without confirmation — a CSV cell can't carry
    // an explicit confirmation attestation (see SpeciesEvidenceSection's
    // confirm checkbox), so an uploaded row can never arrive pre-confirmed.
    // submitAll() hard-blocks on this at submit time; getEntryIssues below
    // picks it up the same way as every other issue here, so it doesn't
    // need its own flag or its own toast.

    setEntries(prev => [...prev, ...withId]);

    // One toast instead of up to five — missing fields, batch/backend
    // duplicate IDs, and unconfirmed reactivity all used to each fire their
    // own long toast.warn() on import, stacking into an unreadable wall.
    // getEntryIssues (the same function the checklist above the table
    // reads from) already has the detail per entry — it doesn't disappear
    // after a few seconds, so the toast's only job is "go look."
    const needsAttention = withId.filter(r => getEntryIssues(r).length > 0).length;
    if (needsAttention > 0) {
      toast.warn(
        `${withId.length} record(s) imported — ${needsAttention} need${needsAttention === 1 ? "s" : ""} attention, see the list above.`,
        { autoClose: 5000 }
      );
    } else {
      toast.success(`${withId.length} record(s) imported from template.`, { autoClose: 4000 });
    }
    scrollTo(tableRef);
  };

  // ── Protocol file ─────────────────────────────────────────────────
  const handleProtocolFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const valid = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!valid.includes(file.type)) { toast.error("Only PDF and Word documents are allowed"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File must be under 10MB"); return; }
    setFileUploading(true);
    setFormData(prev => ({ ...prev, protocol_file: file, file_temp_key: null }));
    try {
      const tempKey = `file_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const payload = new FormData();
      payload.append("protocol_file", file);
      payload.append("submission_temp_key", tempKey);
      const res = await fetch(
        "https://dshb-deposits.its.uiowa.edu/api/upload-protocol-file",
        { method: "POST", body: payload }
      );
      if (!res.ok) throw new Error();
      setFormData(prev => ({
        ...prev, protocol_file: file, protocol_file_name: file.name, file_temp_key: tempKey,
        protocol_file_preview_url: URL.createObjectURL(file),
      }));
      toast.success(`"${file.name}" ready`);
    } catch {
      toast.error("File upload failed");
      setFormData(prev => ({
        ...prev, protocol_file: null, protocol_file_name: null,
        file_temp_key: null, protocol_file_preview_url: null,
      }));
    } finally { setFileUploading(false); }
  };

  // ── UniProt ───────────────────────────────────────────────────────
  const validateUniProt = async (id) => {
    if (!id?.trim()) { setUniprotValid(null); return; }
    setUniprotValid("checking");
    try { setUniprotValid((await fetch(`https://rest.uniprot.org/uniprotkb/${id.trim()}.json`)).ok); }
    catch { setUniprotValid(false); }
  };

  // ── CSV export ────────────────────────────────────────────────────
  const generateCSV = () => {
    if (!draftEntries.length) { toast.error("No data to export"); return; }
    const excludeKeys = new Set(["id", "status", "protocol_file", "file_temp_key", "protocol_file_preview_url"]);
    const allKeys = Array.from(new Set(draftEntries.flatMap(p => Object.keys(p)))).filter(k => !excludeKeys.has(k));
    const escape = (val) => {
      if (val === null || val === undefined) return "";
      if (Array.isArray(val)) val = val.join("; ");
      val = String(val);
      return (val.includes(",") || val.includes('"') || val.includes("\n"))
        ? `"${val.replace(/"/g, '""')}"` : val;
    };
    const csv = [allKeys.join(","), ...draftEntries.map(p =>
      allKeys.map(k => escape(k === "speciesEvidence" ? summarizeEvidence(p[k]) : p[k])).join(",")
    )].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `dshb_deposits_${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(a.href);
    toast.success("CSV downloaded");
  };

  // ── Static data ───────────────────────────────────────────────────
  const applicationsList = [
    "Chromatin Immunoprecipitation","ELISA","FACS","FFPE","Function Blocking",
    "Gel Supershift","Immuno-MRM","Immuno-MS","Immunofluorescence","Immunohistochemistry",
    "Immunoprecipitation","Microarray","Native Western Blot","TRAP","Western Blot",
  ];
  const mandatoryFields = [
    "item_name_number","isotype","antigen","depositor_email","depositor_name_contributor",
    "depositor_institution","host_species","antigen_species","clonality",
  ];
  const pubmedFields = [
    "initial_pubmed_id","epitope_map_pubmed_id","ihc_pubmed_id","if_pubmed_ids",
    "wb_pubmed_id","ip_pubmed_ids","chip_pubmed_ids","elisa_pubmed_ids","ffpe_pubmed_ids",
    "fb_pubmed_ids","microarray_pubmed_ids","trap_pubmed_ids","facs_pubmed_ids",
    "gel_supershift_pubmed_ids","other_pubmed_ids",
  ];
  const formFieldsOrder = [
    // ── Required fields first ──
    "item_name_number","clonality","isotype","antigen","depositor_email",
    "depositor_name_contributor","depositor_institution","host_species","antigen_species",
    // ── Optional fields ──
    "alternative_antigen_names","depositor_growth_medium","depositor_website",
    "positive_tested_species","speciesEvidence","alternate_name_clone_id",
    "additional_information","additional_characterization","depositor_notes","collections",
    "antigen_molecular_weight","gene","alternate_gene_names","entrez_gene_id","uniprot_id",
    "immunogen","immunogen_sequence","myeloma_strain","epitope_mapped",
    "epitope_location_sequence","protocol_file_name","applications",
    ...pubmedFields,
  ];
  const pubmedLabels = {
    initial_pubmed_id:         "Initial Characterization PubMed ID(s)",
    epitope_map_pubmed_id:     "Epitope Mapping PubMed ID(s)",
    ihc_pubmed_id:             "Immunohistochemistry PubMed ID(s)",
    if_pubmed_ids:             "Immunofluorescence PubMed ID(s)",
    wb_pubmed_id:              "Western Blot PubMed ID(s)",
    ip_pubmed_ids:             "Immunoprecipitation PubMed ID(s)",
    chip_pubmed_ids:           "Chromatin Immunoprecipitation PubMed ID(s)",
    elisa_pubmed_ids:          "ELISA PubMed ID(s)",
    ffpe_pubmed_ids:           "FFPE PubMed ID(s)",
    fb_pubmed_ids:             "Function Blocking PubMed ID(s)",
    microarray_pubmed_ids:     "Microarray PubMed ID(s)",
    trap_pubmed_ids:           "TRAP PubMed ID(s)",
    facs_pubmed_ids:           "FACS PubMed ID(s)",
    gel_supershift_pubmed_ids: "Gel Supershift PubMed ID(s)",
    other_pubmed_ids:          "Other PubMed ID(s)",
  };
  const applicationToPubmedMap = {
    "Chromatin Immunoprecipitation":"chip_pubmed_ids","ELISA":"elisa_pubmed_ids",
    "FACS":"facs_pubmed_ids","FFPE":"ffpe_pubmed_ids","Function Blocking":"fb_pubmed_ids",
    "Gel Supershift":"gel_supershift_pubmed_ids","Immunofluorescence":"if_pubmed_ids",
    "Immunohistochemistry":"ihc_pubmed_id","Immunoprecipitation":"ip_pubmed_ids",
    "Microarray":"microarray_pubmed_ids","TRAP":"trap_pubmed_ids","Western Blot":"wb_pubmed_id",
  };

  // ── Handlers ──────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: false }));
    if (name === "uniprot_id") setUniprotValid(null);
    if (name === "item_name_number") setItemNameStatus(null);
    if (name === "alternate_name_clone_id") setCloneIdStatus(null);
    // When clonality changes, clear isotype if it's no longer valid
    if (name === "clonality") {
      const validOptions = value === "Polyclonal" ? POLYCLONAL_ISOTOPES : MONOCLONAL_ISOTOPES;
      if (formData.isotype && !validOptions.includes(formData.isotype)) {
        setFormData(prev => ({ ...prev, [name]: value, isotype: "" }));
        return;
      }
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (titleCaseFields.has(name) && value)
      setFormData(prev => ({ ...prev, [name]: toTitleCase(value) }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      applications: checked
        ? [...prev.applications, value]
        : prev.applications.filter(a => a !== value),
    }));
  };

  const validateForm = () => {
    const errs = {};
    mandatoryFields.forEach(f => {
      if (!formData[f] || !String(formData[f]).trim()) errs[f] = true;
    });
    setErrors(errs);
    if (itemNameStatus?.taken) {
      toast.error("Item Name/Number already exists — please choose a unique name.");
      return false;
    }
    if (cloneIdStatus?.taken) {
      toast.error("Clone ID already exists — please choose a unique identifier.");
      return false;
    }
    // Validate species confirmation — confirmation is mandatory for every
    // species, attaching actual proof is optional (see
    // SpeciesEvidenceSection.js). Still blocks a mid-upload save so a
    // draft never saves with a fileName but no evidenceTempKey.
    if (speciesEvidence.length > 0) {
      const stillUploading = speciesEvidence.some(ev => ev.type === "file" && ev.fileName && !ev.evidenceTempKey);
      if (stillUploading) {
        toast.error("Please wait for the evidence file(s) to finish uploading before saving.", { autoClose: 6000 });
        return false;
      }
      const unconfirmed = speciesEvidence.filter(ev => !ev.confirmed);
      if (unconfirmed.length > 0) {
        const names = unconfirmed.map(ev => ev.species).join(", ");
        toast.error(`Please confirm reactivity for: ${names}`, { autoClose: 6000 });
        return false;
      }
    }
    return !Object.keys(errs).length;
  };

  const buildEntry = () => {
    const c = { ...formData };
    pubmedFields.forEach(f => {
      if (c[f]) c[f] = String(c[f]).split(",").map(s => s.trim()).filter(Boolean).join(", ");
    });
    titleCaseFields.forEach(f => { if (c[f]) c[f] = toTitleCase(c[f]); });
    // Fall back to the already-saved name/key when re-saving an edited draft
    // without touching the file picker — formData.protocol_file (the raw File
    // object) is never restored on edit, only the persisted string fields are.
    c.protocol_file_name = formData.protocol_file?.name || formData.protocol_file_name || null;
    c.file_temp_key             = formData.file_temp_key             || null;
    c.protocol_file_preview_url = formData.protocol_file_preview_url || null;
    delete c.protocol_file;
    c.status = "draft";
    // _itemNameTaken/_cloneIdTaken are set once, at bulk-import time (see
    // handleParsedData), to flag a duplicate Clone ID found back then.
    // editEntry() spreads the whole draft - flags included - into
    // formData, and this function spreads formData right back out, so
    // without clearing them here a fixed, now-unique name would still
    // carry the stale "taken" flag forever. validateForm() has already
    // live-checked itemNameStatus/cloneIdStatus before buildEntry() is
    // ever called, so it's safe to drop both unconditionally here.
    delete c._itemNameTaken;
    delete c._cloneIdTaken;
    // Evidence files are uploaded to the server immediately on selection (see
    // SpeciesEvidenceSection), so all we carry here are plain strings — safe
    // to round-trip through localStorage, unlike a raw File object.
    c.speciesEvidence = speciesEvidence.map(ev => ({
      species:         ev.species,
      type:            ev.type,
      fileName:        ev.fileName        || "",
      evidenceTempKey: ev.evidenceTempKey || "",
      filePreviewUrl:  ev.filePreviewUrl  || "",
      linkUrl:         ev.linkUrl         || "",
      linkLabel:       ev.linkLabel       || "",
      confirmed:       ev.confirmed,
    }));
    return c;
  };

  const resetFormFields = () => {
    setFormData(initialFormState);
    setUniprotValid(null);
    setItemNameStatus(null);
    setCloneIdStatus(null);
    setSpeciesEvidence([]);
    resetAll();
    setErrors({});
  };

  const saveDraft = () => {
    if (!validateForm()) { toast.error("Please fill all required fields."); return; }
    if (editingEntryId !== null) {
      setEntries(prev => prev.map(e => e.id === editingEntryId ? { ...buildEntry(), id: editingEntryId } : e));
      toast.success("Draft updated.");
    } else {
      const newId = Date.now();
      setEntries(prev => [...prev, { ...buildEntry(), id: newId }]);
      setEditingEntryId(newId);
      toast.success("Draft saved. You can keep editing or add another.");
    }
  };

  const saveAndAddAnother = () => {
    if (!validateForm()) { toast.error("Please fill all required fields."); return; }
    if (editingEntryId !== null) {
      setEntries(prev => prev.map(e => e.id === editingEntryId ? { ...buildEntry(), id: editingEntryId } : e));
    } else {
      setEntries(prev => [...prev, { ...buildEntry(), id: Date.now() }]);
    }
    setEditingEntryId(null);
    resetFormFields();
    toast.success("Saved. Add the next antibody below.");
    scrollTo(formRef);
  };

  const submitAll = async () => {
    if (!draftEntries.length) { toast.error("No draft entries to submit."); return; }

    // The Submit All button is already disabled whenever entriesWithIssues
    // is non-empty (missing mandatory fields, a duplicate Clone ID, or an
    // unconfirmed species reactivity — see getEntryIssues, the single
    // source of truth the checklist above the table also reads from). This
    // check is just the safety net for a stale button state; the toast
    // stays short because the checklist already lists every entry and the
    // exact reason, with no 8-second window to read it in.
    if (entriesWithIssues.length > 0) {
      toast.error(
        `${entriesWithIssues.length} ${entriesWithIssues.length === 1 ? "entry needs" : "entries need"} attention — see the list above.`,
        { autoClose: 5000 }
      );
      return;
    }

    await doSubmit(draftEntries);
  };

  const doSubmit = async (products) => {
    setSubmitting(true);
    try {
      const userName = products[0]?.depositor_name_contributor;
      const authEmail = user?.email;
      if (!authEmail) { toast.error("You must be logged in to submit."); setSubmitting(false); return; }
      const cleaned = products.map(p => { const c = { ...p }; delete c.status; c.status = "Pending"; return c; });
      const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/deposit-submit", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ depositor: userName, email: authEmail, products: cleaned }),
      });
      if (!res.ok) throw new Error((await res.json()).message || "Submission failed");

      // Upload species evidence files for each submitted product
      const submissionRes = await res.json().catch(() => ({}));
      const submissionIds = submissionRes.submission_ids || [];
      for (let i = 0; i < products.length; i++) {
        const p = products[i];
        const sid = submissionIds[i] || null;
        if (!sid) continue;
        // Find evidence for this product's species
        const evidence = p.id === editingEntryId
          ? speciesEvidence
          : (p.speciesEvidence || []);
        for (const ev of evidence) {
          if (!ev.confirmed) continue;
          try {
            const fd = new FormData();
            fd.append("submission_id", sid);
            fd.append("species", ev.species);
            fd.append("evidence_type", ev.type);
            fd.append("confirmed", "1");
            if (ev.type === "file" && ev.evidenceTempKey) {
              fd.append("evidence_temp_key", ev.evidenceTempKey);
            } else if (ev.type === "link") {
              fd.append("link_url",   ev.linkUrl   || "");
              fd.append("link_label", ev.linkLabel || "");
            }
            await fetch("https://dshb-deposits.its.uiowa.edu/api/species-evidence", {
              method: "POST", body: fd,
            });
          } catch (evErr) {
            console.error("Evidence upload failed for", ev.species, evErr.message);
          }
        }
      };
      const submittedIds = new Set(products.map(p => p.id));
      setEntries(prev => prev.filter(e => !submittedIds.has(e.id)));
      setEditingEntryId(null);
      resetFormFields();
      setSubmitSuccess(true);
      scrollTo(formTopRef);
      setTimeout(() => setSubmitSuccess(false), 10000);
    } catch (err) {
      toast.error(err.message || "Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  const requestCancel = () => {
    const hasContent = Object.entries(formData).some(([k, v]) => {
      if (k === "clonality") return false;
      if (Array.isArray(v)) return v.length > 0;
      return v !== "" && v !== null;
    });
    if (editingEntryId !== null || hasContent) { setShowCancelConfirm(true); }
    else { resetFormFields(); }
  };

  const confirmCancel = () => {
    setEditingEntryId(null);
    resetFormFields();
    setShowCancelConfirm(false);
    toast.info("Changes discarded.");
    scrollTo(formRef);
  };

  const editEntry = (id) => {
    const e = entries.find(e => e.id === id);
    if (!e) return;
    setFormData({ ...initialFormState, ...e });
    setSpeciesEvidence(e.speciesEvidence || []);
    setEditingEntryId(id);
    setUniprotValid(null);
    setItemNameStatus(null);
    setCloneIdStatus(null);
    resetAll();
    setErrors({});
    scrollTo(formRef);
  };

  const deleteEntry = (id) => {
    setEntries(prev => prev.filter(e => e.id !== id));
    if (editingEntryId === id) { setEditingEntryId(null); resetFormFields(); }
    toast.success("Entry removed.");
  };

  const renderInput = (label, name, placeholder, tourRef) => (
    <>
      <label>{label}{mandatoryFields.includes(name) && <span className="required"> *</span>}</label>
      <input ref={tourRef} type="text" name={name} value={formData[name]}
        onChange={handleChange}
        onBlur={titleCaseFields.has(name) ? handleBlur : undefined}
        placeholder={placeholder}
        className={errors[name] ? "input-error" : ""}
      />
    </>
  );

  const columnLabelOverrides = { speciesEvidence: "Species Evidence" };
  const colLabel = (key) => columnLabelOverrides[key] || key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());

  const summarizeEvidence = (evidence) => {
    if (!Array.isArray(evidence) || evidence.length === 0) return "";
    return evidence.map(ev => {
      const proof = ev.type === "file" ? (ev.fileName || "file") : (ev.linkLabel || ev.linkUrl || "link");
      return `${ev.species}: ${proof}`;
    }).join("; ");
  };

  // Species + a clickable proof (opens in a new tab) for the Draft Entries table.
  // Prefers the local blob preview (instant, no request) but falls back to the
  // server's cache-by-temp-key endpoint, which survives a page reload — the
  // blob URL does not. Both go stale once the file is actually submitted or
  // the server's 30-min cache window expires, whichever comes first.
  const renderEvidenceCell = (evidence) => {
    if (!Array.isArray(evidence) || evidence.length === 0) return "—";

    // A species can be confirmed with no proof attached at all — evidence
    // objects default to type:"file" even when nothing was ever uploaded,
    // so fileName/linkUrl being empty is the real signal, not ev.type.
    // Confirmation status itself is shown on the Positive Tested Species
    // column instead, so this column only ever needs to say whether real
    // proof exists — nothing has proof at all collapses to a plain "—".
    const withProof = evidence.map(ev => {
      const hasFile = ev.type === "file" && !!ev.fileName;
      const hasLink = ev.type === "link" && !!(ev.linkUrl || ev.linkLabel);
      return { ev, hasFile, hasLink, hasProof: hasFile || hasLink };
    });
    if (!withProof.some(x => x.hasProof)) return "—";

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
        {withProof.map(({ ev, hasFile, hasLink, hasProof }, i) => {
          const proofLabel = hasFile ? ev.fileName : hasLink ? (ev.linkLabel || ev.linkUrl) : "No evidence";
          const href = hasFile
            ? (ev.filePreviewUrl || (ev.evidenceTempKey ? `https://dshb-deposits.its.uiowa.edu/api/preview-cached-file/${ev.evidenceTempKey}` : null))
            : hasLink ? ev.linkUrl : null;
          return (
            <div key={i} style={{ whiteSpace: "nowrap" }}>
              <span style={{ fontWeight: 600 }}>{ev.species}: </span>
              {href ? (
                <a href={href} target="_blank" rel="noreferrer" style={{ color: "#305DBD" }}
                  onClick={(e) => e.stopPropagation()}>
                  {proofLabel}
                </a>
              ) : (
                <span style={{ color: hasProof ? undefined : "#9ca3af" }}>{proofLabel}</span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const isotypeOptions = formData.clonality === "Polyclonal" ? POLYCLONAL_ISOTOPES : formData.clonality === "Monoclonal" ? MONOCLONAL_ISOTOPES : [...MONOCLONAL_ISOTOPES];

  const missingFieldLabels = {
    item_name_number: "Item Name / Number", isotype: "Isotype", antigen: "Antigen",
    depositor_email: "Depositor Email", depositor_name_contributor: "Depositor Name",
    depositor_institution: "Depositor Institution", host_species: "Host Species",
    antigen_species: "Antigen Species", clonality: "Clonality",
  };

  // Every reason a draft entry can't be submitted yet, as plain-language
  // strings. Used by both the Draft Entries table rows (Edit button label)
  // and the summary checklist above the table, so the two always agree —
  // same source submitAll() already blocks on, just surfaced before the
  // click instead of only in a toast after it.
  const getEntryIssues = (p) => {
    const issues = [];
    mandatoryFields.forEach(f => {
      if (!p[f] || !String(p[f]).trim()) issues.push(missingFieldLabels[f] || colLabel(f));
    });
    if (p._itemNameTaken) issues.push("Clone ID already in use");
    if (p._cloneIdTaken) issues.push("Alternate Name/Clone ID already in use");
    const positiveSpecies = parseSpecies(p.positive_tested_species);
    const reactivityMissing = positiveSpecies.some(sp => {
      const ev = (p.speciesEvidence || []).find(e => e.species === sp);
      return !ev || !ev.confirmed;
    });
    if (reactivityMissing) issues.push("Species reactivity not confirmed");
    return issues;
  };

  const entriesWithIssues = draftEntries
    .map(p => ({ entry: p, issues: getEntryIssues(p) }))
    .filter(x => x.issues.length > 0);

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="form-container" ref={formTopRef}>
      {/* Toasts render via the single global <ToastContainer> in App.js —
          a second one here used to double-fire every toast.success/error,
          each half styled differently (App.js's theme="colored" vs this
          file's unthemed default), which is why one action looked like it
          fired a green AND a white toast for the same message. */}

      <div style={{ marginBottom: "28px" }}>
        <h1 style={{ color: "#1a2c5b", fontSize: "22px", fontWeight: 700, margin: "0 0 4px" }}>
          Antibody Submission Form
        </h1>
        <p style={{ color: "#6b7280", fontSize: "13px", margin: 0 }}>
          Developmental Studies Hybridoma Bank · University of Iowa
        </p>
      </div>

      {submitSuccess && (
        <div style={{
          background: "#f0fdf4", border: "1px solid #86efac", borderLeft: "4px solid #16a34a",
          borderRadius: "8px", padding: "16px 20px", marginBottom: "24px",
          display: "flex", alignItems: "flex-start", gap: "12px",
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%", background: "#16a34a", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, flexShrink: 0,
          }}>✓</div>
          <div style={{ flex: 1 }}>
            <strong style={{ color: "#15803d", display: "block", marginBottom: "4px" }}>Submission received</strong>
            <p style={{ color: "#166534", fontSize: "13px", margin: "0 0 10px" }}>
              Thank you. DSHB will review your submission and contact you via email.
            </p>
            <button onClick={() => setSubmitSuccess(false)} style={{
              background: "none", border: "1px solid #16a34a", borderRadius: "5px",
              color: "#16a34a", padding: "5px 12px", cursor: "pointer", fontSize: "12px", fontWeight: 600,
            }}>Submit another antibody</button>
          </div>
          <button onClick={() => setSubmitSuccess(false)} style={{
            background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: "16px",
          }}>✕</button>
        </div>
      )}

      {/* Template bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: "6px",
        padding: "10px 14px", background: "#f8fafc", border: "1px solid #e2e8f0",
        borderRadius: "7px", marginBottom: "16px", fontSize: "13px",
      }}>
        <span style={{ color: "#6b7280" }}>Prefer to use a template?</span>
        <button ref={setTourRef("downloadTemplate")} className="text-link" onClick={downloadTemplate} style={{ fontWeight: 600 }}>
          Download template
        </button>
        <span style={{ color: "#d1d5db" }}>·</span>
        <label ref={setTourRef("uploadTemplate")} className="text-link" style={{ cursor: "pointer", fontWeight: 600 }}
          onClick={() => fileInputRef.current?.click()}>
          Upload filled template
        </label>
        <input
          ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const ext = file.name.split(".").pop().toLowerCase();
            if (ext !== "xlsx" && ext !== "xls" && ext !== "csv") {
              toast.error("Only Excel (.xlsx, .xls) or CSV (.csv) files are supported"); e.target.value = ""; return;
            }
            import("xlsx").then(XLSX => {
              const reader = new FileReader();
              reader.onload = (evt) => {
                try {
                  const wb = ext === "csv"
                    ? XLSX.read(evt.target.result, { type: "string" })
                    : XLSX.read(new Uint8Array(evt.target.result), { type: "array" });
                  const sheetName = wb.SheetNames.find(n => !n.startsWith("_")) || wb.SheetNames[0];
                  const allRows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, defval: "" });
                  if (allRows.length < 2) { toast.error("No data found in file."); return; }
                  const headers = allRows[0];

                  // Stringify a row the same consistent way whether it
                  // came from the canonical template or the depositor's
                  // uploaded copy, so the two can be compared directly.
                  const rowKey = (row) => row.map(c => String(c ?? "").trim().toLowerCase()).join("|");

                  const finishParsing = (exampleRowKey) => {
                    const dataRows = allRows.slice(1).filter(row => rowKey(row) !== exampleRowKey);
                    const nonEmpty = dataRows.filter(row =>
                      row.some(c => c !== null && c !== undefined && String(c).trim() !== "")
                    );
                    if (!nonEmpty.length) {
                      toast.error("No data rows found. Please fill in at least one row below the example."); return;
                    }
                    const json = nonEmpty.map(row => {
                      const obj = {};
                      headers.forEach((h, i) => { if (h) obj[String(h).trim()] = row[i] ?? ""; });
                      return obj;
                    });
                    const mapped = json.map(mapExcelRow).filter(r =>
                      Object.values(r).some(v =>
                        v !== null && v !== undefined &&
                        (Array.isArray(v) ? v.length > 0 : String(v).trim() !== "")
                      )
                    );
                    if (!mapped.length) { toast.error("No data found. Please fill in the template before uploading."); return; }
                    handleParsedData(mapped);
                  };

                  // UPDATED: was a hardcoded marker string ("anti-gapdh")
                  // matched against the whole row's text -- broke the
                  // moment the template's example row content changed
                  // (it's now MF20/Myosin Heavy Chain, no "gapdh" in it
                  // anywhere, so the old filter matched nothing and the
                  // example row got uploaded as if it were real data).
                  // Comparing against the row the CURRENT live template
                  // actually contains makes this self-updating -- it
                  // keeps working automatically the next time the
                  // template's example row is edited, with nothing to
                  // remember to update here.
                  fetch(`${process.env.PUBLIC_URL}/DSHB_Antibody_Template.xlsx`)
                    .then(r => r.arrayBuffer())
                    .then(buf => {
                      const templateWb = XLSX.read(new Uint8Array(buf), { type: "array" });
                      const templateSheetName = templateWb.SheetNames.find(n => !n.startsWith("_")) || templateWb.SheetNames[0];
                      const templateRows = XLSX.utils.sheet_to_json(templateWb.Sheets[templateSheetName], { header: 1, defval: "" });
                      finishParsing(rowKey(templateRows[1] || []));
                    })
                    // If the canonical template can't be fetched, don't
                    // block the upload over it -- just skip the
                    // example-row exclusion step (same behavior as
                    // before this feature existed at all).
                    .catch(() => finishParsing(null));
                } catch { toast.error("Error reading file."); }
              };
              if (ext === "csv") reader.readAsText(file);
              else reader.readAsArrayBuffer(file);
            });
            e.target.value = "";
          }}
        />
      </div>

      {/* First-time helper */}
      {draftEntries.length === 0 && (
        <div style={{
          display: "flex", gap: "10px", alignItems: "flex-start",
          padding: "12px 16px", marginBottom: "16px",
          background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: "8px",
          fontSize: "12px", color: "#075985", lineHeight: 1.6,
        }}>
          <span style={{ fontSize: "16px", flexShrink: 0 }}>💡</span>
          <span>Fill in the form below and click <strong>Save</strong> for each antibody.
            Once you've added everything, click <strong>Submit All</strong>.
            For instructions or additional information, click <strong>Help</strong> in the top navigation.
          </span>
        </div>
      )}

      {/* Draft Entries table */}
      <div ref={tableRef} style={{ border: "1px solid #e2e8f0", borderRadius: "8px", marginBottom: "32px", overflow: "hidden" }}>
        <div ref={setTourRef("draftEntries")} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontWeight: 600, color: "#1a2c5b", fontSize: "14px" }}>Draft Entries</span>
            <span style={{
              background: draftEntries.length > 0 ? "#1a2c5b" : "#e2e8f0",
              color: draftEntries.length > 0 ? "#fff" : "#9ca3af",
              borderRadius: "20px", padding: "1px 10px", fontSize: "12px", fontWeight: 600,
            }}>{draftEntries.length}</span>
            {onStartTour && (
              <button onClick={onStartTour} title="Replay the welcome tour" style={{
                background: "none", border: "none", cursor: "pointer", color: "#9ca3af",
                fontSize: "11px", fontWeight: 600, textDecoration: "underline", padding: 0,
              }}>? Tour</button>
            )}
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {draftEntries.length > 0 && (
              <button ref={setTourRef("exportCsv")} onClick={generateCSV} style={{
                display: "flex", alignItems: "center", gap: "5px",
                background: "none", border: "1px solid #d1d5db", borderRadius: "5px",
                padding: "5px 12px", cursor: "pointer", fontSize: "12px", color: "#374151", fontWeight: 500,
              }}><FaDownload style={{ fontSize: "11px" }} /> Export CSV</button>
            )}
            <button
              ref={setTourRef("submitAll")}
              onClick={submitAll}
              disabled={!draftEntries.length || submitting || entriesWithIssues.length > 0}
              title={entriesWithIssues.length > 0 ? "Resolve the entries listed below first" : undefined}
              style={{
                background: draftEntries.length > 0 && !entriesWithIssues.length ? "#1a2c5b" : "#f3f4f6",
                color: draftEntries.length > 0 && !entriesWithIssues.length ? "#fff" : "#9ca3af",
                border: "none", borderRadius: "5px", padding: "5px 16px",
                cursor: draftEntries.length > 0 && !entriesWithIssues.length ? "pointer" : "not-allowed",
                fontSize: "12px", fontWeight: 600,
              }}>{submitting ? "Submitting…" : "Submit All"}</button>
          </div>
        </div>
        {draftEntries.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 20px", borderBottom: "1px solid #e2e8f0", fontSize: "11px", color: "#9ca3af", flexWrap: "wrap" }}>
            <span>✓ Auto-saved — find drafts anytime under <strong style={{ color: "#6b7280" }}>History → Drafts</strong></span>
            <span style={{ color: "#d1d5db" }}>·</span>
            <span style={{ color: entriesWithIssues.length === 0 ? "#15803d" : "#6b7280", fontWeight: 600 }}>
              {entriesWithIssues.length === 0
                ? `All ${draftEntries.length} ready to submit`
                : `${draftEntries.length - entriesWithIssues.length} of ${draftEntries.length} ready to submit`}
            </span>
          </div>
        )}
        {entriesWithIssues.length > 0 && (
          <>
            <button
              onClick={() => setAttentionExpanded(v => !v)}
              style={{
                width: "100%", textAlign: "left", background: "#fffbeb", border: "none",
                borderBottom: attentionExpanded ? "none" : "1px solid #fde68a",
                padding: "10px 20px", cursor: "pointer",
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px",
              }}>
              <span style={{ fontSize: "12.5px", fontWeight: 700, color: "#92400e" }}>
                {entriesWithIssues.length} {entriesWithIssues.length === 1 ? "entry needs" : "entries need"} to be edited before you can Submit All
              </span>
              <span style={{ fontSize: "12px", fontWeight: 600, color: "#92400e" }}>{attentionExpanded ? "▴ Hide" : "▾ Show"}</span>
            </button>
            {attentionExpanded && (
              <div style={{ padding: "0 20px 12px", background: "#fffbeb", borderBottom: "1px solid #fde68a", display: "flex", flexDirection: "column", gap: "8px" }}>
                {entriesWithIssues.map(({ entry, issues }) => (
                  <div key={entry.id} style={{ display: "flex", flexDirection: "column", gap: "6px", padding: "8px 10px", background: "#fff", border: "1px solid #fde68a", borderRadius: "6px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                      <span style={{ fontSize: "12.5px", color: "#374151" }}>
                        <strong>{entry.item_name_number || "(unnamed)"}</strong>
                        {" — "}{issues.length} required item{issues.length > 1 ? "s" : ""} missing
                      </span>
                      <button onClick={() => editEntry(entry.id)} style={{
                        background: "none", border: "none", padding: 0, cursor: "pointer",
                        fontSize: "12px", fontWeight: 600, color: "#92400e", textDecoration: "underline",
                      }}>Edit to Complete →</button>
                    </div>
                    {issues.length > 1 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {issues.map(issue => (
                          <span key={issue} style={{ background: "#fde68a", color: "#78350f", borderRadius: "999px", padding: "2px 9px", fontSize: "11px", fontWeight: 600 }}>{issue}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {draftEntries.length === 0 ? (
          <div style={{ padding: "40px 20px", textAlign: "center" }}>
            <p style={{ color: "#9ca3af", fontSize: "14px", margin: "0 0 4px", fontWeight: 500 }}>No entries yet</p>
            <p style={{ color: "#d1d5db", fontSize: "12px", margin: 0 }}>Complete the form below and click Save to add entries here</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto", position: "relative" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <thead>
                  <tr>
                    {formFieldsOrder.map(f => (
                      <th key={f} style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, background: "#f8fafc", color: "#374151", fontSize: "11px", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{colLabel(f)}</th>
                    ))}
                    <th style={{ padding: "8px 10px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", position: "sticky", right: 0, zIndex: 2, boxShadow: "-2px 0 4px rgba(0,0,0,0.06)", fontSize: "11px", fontWeight: 600, color: "#374151" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {draftEntries.map((p, idx) => {
                    const issues = getEntryIssues(p);
                    const needsAttention = issues.length > 0;
                    const rowBg = p.id === editingEntryId ? "#eff6ff" : needsAttention ? "#fff7ed" : (idx % 2 === 0 ? "#fff" : "#fafafa");
                    return (
                    <React.Fragment key={p.id}>
                      <tr style={{ background: rowBg }}>
                        {formFieldsOrder.map(f => (
                          <td key={f} style={f === "speciesEvidence"
                            ? { padding: "8px 10px", borderBottom: "1px solid #f3f4f6", color: "#374151", minWidth: "160px", verticalAlign: "top" }
                            : { padding: "8px 10px", borderBottom: "1px solid #f3f4f6", color: "#374151", minWidth: "160px", maxWidth: "280px", whiteSpace: "normal", wordBreak: "break-word", verticalAlign: "top" }}>
                            {f === "applications" || f === "collections" ? (Array.isArray(p[f]) ? p[f].join(", ") : p[f]) || "—"
                              : f === "speciesEvidence" ? renderEvidenceCell(p[f])
                              : f === "positive_tested_species" ? (p[f]
                                  ? <>
                                      <div>{p[f]}</div>
                                      {issues.includes("Species reactivity not confirmed") && (
                                        <div style={{ color: "#dc2626", fontSize: "11px", fontWeight: 600, marginTop: "4px" }}>Not confirmed</div>
                                      )}
                                    </>
                                  : "—")
                              : f === "protocol_file_name" ? (p[f]
                                  ? (() => {
                                      const href = p.protocol_file_preview_url
                                        || (p.file_temp_key ? `https://dshb-deposits.its.uiowa.edu/api/preview-cached-file/${p.file_temp_key}` : null);
                                      return href
                                        ? <a href={href} target="_blank" rel="noreferrer"
                                            style={{ color: "#305DBD" }} onClick={(e) => e.stopPropagation()}>{p[f]}</a>
                                        : p[f];
                                    })()
                                  : "—")
                              : p[f] || "—"}
                          </td>
                        ))}
                        <td style={{ padding: "8px 12px", borderBottom: "1px solid #f3f4f6", position: "sticky", right: 0, zIndex: 1, background: rowBg, boxShadow: "-2px 0 4px rgba(0,0,0,0.06)" }}>
                          <div style={{ display: "flex", flexWrap: "nowrap", gap: "8px", alignItems: "center" }}>
                            {needsAttention ? (
                              <button onClick={() => editEntry(p.id)} title={issues.join(" · ")} style={{
                                flexShrink: 0, minWidth: "150px", textAlign: "center", whiteSpace: "nowrap",
                                background: "#f59e0b", color: "#fff", border: "none",
                                borderRadius: "5px", padding: "5px 10px", cursor: "pointer",
                                fontSize: "11.5px", fontWeight: 600,
                              }}>Edit to Complete{issues.length > 1 ? ` (${issues.length})` : ""}</button>
                            ) : (
                              <button onClick={() => editEntry(p.id)} style={{
                                flexShrink: 0, minWidth: "150px", textAlign: "center", whiteSpace: "nowrap",
                                background: "#fff", border: "1px solid #d1d5db", borderRadius: "5px",
                                padding: "5px 10px", cursor: "pointer", color: "#374151",
                                fontSize: "11.5px", fontWeight: 600,
                              }}>Edit</button>
                            )}
                            <button onClick={() => deleteEntry(p.id)} style={{
                              flexShrink: 0, whiteSpace: "nowrap", background: "#fff", border: "1px solid #d1d5db", borderRadius: "5px",
                              padding: "5px 10px", cursor: "pointer", color: "#dc2626",
                              fontSize: "11.5px", fontWeight: 600,
                            }}>Delete</button>
                            {pubmedFields.some(f => p[f]) && (
                              <button onClick={() => setShowPubmedIds(prev => ({ ...prev, [p.id]: !prev[p.id] }))} title="View PubMed IDs" style={{ flexShrink: 0, background: "none", border: "none", cursor: "pointer", color: showPubmedIds[p.id] ? "#2b6cb0" : "#9ca3af", fontSize: "14px", padding: "2px" }}><FaFileAlt /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {showPubmedIds[p.id] && (
                        <tr>
                          <td colSpan={formFieldsOrder.length + 1} style={{ padding: "10px 16px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                              {pubmedFields.filter(f => p[f]).map(f => (
                                <div key={f} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "5px", padding: "4px 10px", fontSize: "11px" }}>
                                  <span style={{ color: "#6b7280", fontWeight: 600 }}>{f.replace(/_/g, " ")}: </span>
                                  <span style={{ color: "#1a2c5b" }}>{p[f]}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Form */}
      <div ref={formRef}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", paddingBottom: "12px", borderBottom: "1px solid #e2e8f0" }}>
          <div>
            <h2 style={{ margin: "0 0 2px", fontSize: "15px", color: "#1a2c5b", fontWeight: 600 }}>
              {editingEntryId !== null ? "Editing Draft" : "Add Antibody"}
            </h2>
            <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af" }}>
              Fields marked <span style={{ color: "#dc2626" }}>*</span> are required
            </p>
          </div>
          {editingEntryId !== null && (
            <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: "6px", padding: "6px 12px", fontSize: "12px", color: "#92400e", fontWeight: 500 }}>
              Editing — click Save or Cancel
            </div>
          )}
        </div>

        <form style={{ marginBottom: "20px" }}>

          {/* ── Item Name / Number — with uniqueness check + searchability hint ── */}
          <>
            <label>Item Name / Number <span className="required"> *</span></label>
            <div ref={setTourRef("itemName")}>
              <input
                type="text" name="item_name_number" value={formData.item_name_number}
                onChange={handleChange}
                onBlur={() => checkItemName(formData.item_name_number)}
                placeholder="e.g., MF20, 4F2, anti-GAPDH"
                className={errors.item_name_number || itemNameStatus?.taken ? "input-error" : ""}
                style={{ width: "100%", ...(itemNameStatus === "available" ? { borderColor: "#3EB489" } : {}) }}
              />
              {/* Hint below the input, not between label and input */}
              <p style={{ margin: "4px 0 0", fontSize: "11px", color: "#305DBD", fontStyle: "italic" }}>
                💡 For ease of searchability, consider not using spaces (e.g., "MF20" not "MF 20"). Max 28 characters recommended.
              </p>
              {itemNameStatus === "checking" && <p style={{ fontSize: "11px", color: "#6b7280", margin: "3px 0 0" }}>Checking availability…</p>}
              {itemNameStatus === "available" && <p style={{ fontSize: "11px", color: "#3EB489", margin: "3px 0 0", fontWeight: 600 }}>✓ Name is available</p>}
              {itemNameStatus?.taken && (
                <div style={{ marginTop: "6px", padding: "10px 14px", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "7px", fontSize: "12px" }}>
                  <p style={{ margin: "0 0 6px", color: "#dc2626", fontWeight: 700 }}>✕ {itemNameStatus.message}</p>
                  {itemNameStatus.suggestions?.length > 0 && (
                    <>
                      <p style={{ margin: "0 0 6px", color: "#374151" }}>Suggested alternatives — click to use one:</p>
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "8px" }}>
                        {itemNameStatus.suggestions.map(s => (
                          <button key={s} type="button"
                            onClick={() => { setFormData(prev => ({ ...prev, item_name_number: s })); setItemNameStatus(null); setTimeout(() => checkItemName(s), 100); }}
                            style={{ padding: "4px 12px", background: "#305DBD", color: "#fff", border: "none", borderRadius: "20px", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  {itemNameStatus.tip && (
                    <p style={{ margin: 0, fontSize: "11px", color: "#6b7280", fontStyle: "italic", borderTop: "1px solid #fecaca", paddingTop: "6px" }}>
                      💡 {itemNameStatus.tip}
                    </p>
                  )}
                </div>
              )}
            </div>
          </>

          {/* ── Clonality — ABOVE Isotype so it filters the isotype list ── */}
          <label>Clonality <span className="required">*</span></label>
          <select name="clonality" value={formData.clonality} onChange={handleChange}
            className={errors.clonality ? "input-error" : ""}>
            <option value="">Select Clonality</option>
            <option value="Monoclonal">Monoclonal</option>
            <option value="Polyclonal">Polyclonal</option>
          </select>

          {/* ── Isotype — options filtered by clonality selection ── */}
          <label>Isotype <span className="required">*</span></label>
          <select name="isotype" value={formData.isotype} onChange={handleChange}
            className={errors.isotype ? "input-error" : ""}>
            <option value="">Select Isotype</option>
            {isotypeOptions.map(iso => <option key={iso} value={iso}>{iso}</option>)}
          </select>

          {renderInput("Antigen", "antigen", "e.g., Myosin Heavy Chain, p53")}

          {/* ── Required fields near top ── */}
          <label>Depositor(s) Email <span className="required">*</span></label>
          <input type="text" name="depositor_email" value={formData.depositor_email}
            onChange={handleChange} placeholder="your@email.com"
            className={errors.depositor_email ? "input-error" : ""} />

          <label>Depositor(s) Name <span className="required">*</span></label>
          <input type="text" name="depositor_name_contributor"
            value={formData.depositor_name_contributor}
            onChange={handleChange} onBlur={handleBlur}
            placeholder="Last, First Initial — e.g., Smith, J.; Doe, A."
            className={errors.depositor_name_contributor ? "input-error" : ""} />

          {/* ── Institution — with worldwide autocomplete via ROR API, plus
               an explicit manual-entry fallback for anyone who'd rather
               just type a name than deal with a search dropdown ── */}
          <label>Depositor(s) Institution <span className="required">*</span></label>
          <div ref={setTourRef("institution")} style={{ position: "relative" }}>
            {!instManualEntry ? (
              <>
                <input
                  type="text" name="depositor_institution"
                  value={formData.depositor_institution}
                  onChange={(e) => { handleChange(e); searchInstitutions(e.target.value); }}
                  onBlur={() => setTimeout(() => setShowInstDropdown(false), 200)}
                  onFocus={() => { if (institutionSuggestions.length > 0) setShowInstDropdown(true); }}
                  placeholder="Start typing to search, e.g., University of Iowa"
                  autoComplete="off"
                  className={errors.depositor_institution ? "input-error" : ""}
                />
                {showInstDropdown && institutionSuggestions.length > 0 && (
                  <div style={{
                    position: "absolute", left: 0, right: 0, top: "100%",
                    background: "#fff", border: "1px solid #D4D8DF", borderRadius: "8px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 9999,
                    maxHeight: "240px", overflowY: "auto",
                  }}>
                    {institutionSuggestions.map((inst, i) => (
                      <InstDropdownItem
                        key={i} inst={inst}
                        onSelect={() => {
                          setFormData(prev => ({ ...prev, depositor_institution: inst.name }));
                          setShowInstDropdown(false);
                          setInstitutionSuggestions([]);
                        }}
                      />
                    ))}
                  </div>
                )}
                <button type="button"
                  onClick={() => { setInstManualEntry(true); setShowInstDropdown(false); setInstitutionSuggestions([]); }}
                  style={{
                    display: "block", marginTop: "4px", padding: 0,
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: "11px", color: "#305DBD", fontWeight: 700, textDecoration: "underline",
                  }}>
                  Can't find your institution? Enter it manually
                </button>
              </>
            ) : (
              <>
                <input
                  type="text" name="depositor_institution"
                  value={formData.depositor_institution}
                  onChange={handleChange}
                  placeholder="Type your institution's full name"
                  autoComplete="off"
                  className={errors.depositor_institution ? "input-error" : ""}
                />
                <p style={{ margin: "4px 0 0", fontSize: "11px", color: "#9ca3af" }}>
                  ✓ Manual entry — whatever you type here is saved exactly as entered.{" "}
                  <button type="button"
                    onClick={() => setInstManualEntry(false)}
                    style={{
                      background: "none", border: "none", padding: 0, cursor: "pointer",
                      fontSize: "11px", color: "#305DBD", fontWeight: 700, textDecoration: "underline",
                    }}>
                    Back to search
                  </button>
                </p>
              </>
            )}
          </div>

          {renderInput("Host Species",          "host_species",      "e.g., Mouse, Rabbit")}
          {renderInput("Antigen Species",        "antigen_species",   "e.g., Human, Mouse")}

          <label>Collections</label>
          <CollectionsMultiSelect
            value={formData.collections}
            onChange={(vals) => setFormData(prev => ({ ...prev, collections: vals }))}
          />

          {/* ── Optional fields ── */}
          {renderInput("Alternative Antigen Names", "alternative_antigen_names", "e.g., anti-slit, anti-Slit-C")}
          {renderInput("Depositor(s) Website", "depositor_website", "e.g., https://lab.university.edu")}
          {renderInput("Positive Tested Species","positive_tested_species", "e.g., Human, Rat", setTourRef("positiveSpecies"))}

          {/* ── Species Reactivity Evidence ── */}
          <SpeciesEvidenceSection
            speciesString={formData.positive_tested_species}
            evidence={speciesEvidence}
            onChange={setSpeciesEvidence}
          />
          {renderInput("Molecular Weight",       "antigen_molecular_weight","e.g., 220 kDa")}
          {renderInput("Gene",                   "gene",              "Gene symbol")}
          {renderInput("Alternate Gene Names",   "alternate_gene_names","Other gene symbols")}
          {renderInput("Entrez Gene ID",         "entrez_gene_id",    "e.g., 7157")}

          {/* ── Clone ID — char limit + duplicate check ── */}
          <label>Alternate Name / Clone ID</label>
          <div>
            <input
              type="text" name="alternate_name_clone_id"
              value={formData.alternate_name_clone_id}
              onChange={handleChange}
              onBlur={() => checkCloneId(formData.alternate_name_clone_id)}
              placeholder="e.g., 4B2, Clone-7"
              style={{ width: "100%", borderColor: cloneIdStatus?.taken ? "#dc2626" : cloneIdStatus === "available" ? "#3EB489" : undefined }}
            />
            {formData.alternate_name_clone_id.length > 0 && (
              <p style={{ fontSize: "11px", margin: "2px 0 0", color: formData.alternate_name_clone_id.length > 28 ? "#dc2626" : "#9ca3af" }}>
                {formData.alternate_name_clone_id.length}/28 characters
                {formData.alternate_name_clone_id.length > 28 ? " — exceeds recommended limit" : ""}
              </p>
            )}
            {cloneIdStatus === "checking" && <p style={{ fontSize: "11px", color: "#6b7280", margin: "3px 0 0" }}>Checking availability…</p>}
            {cloneIdStatus === "available" && <p style={{ fontSize: "11px", color: "#3EB489", margin: "3px 0 0", fontWeight: 600 }}>✓ Clone ID is available</p>}
            {cloneIdStatus?.taken && (
              <p style={{ fontSize: "12px", color: "#dc2626", margin: "4px 0 0", padding: "6px 10px", background: "#fef2f2", borderRadius: "6px", border: "1px solid #fca5a5" }}>
                ✕ This Clone ID already exists in DSHB records. Please choose a unique identifier.
              </p>
            )}
          </div>

          {renderInput("Myeloma Strain", "myeloma_strain", "e.g., Sp2/0")}

          {/* ── UniProt ── */}
          <label>UniProt ID</label>
          <div ref={setTourRef("uniprot")}>
            <input type="text" name="uniprot_id" value={formData.uniprot_id}
              onChange={handleChange} onBlur={() => validateUniProt(formData.uniprot_id)}
              placeholder="e.g., P04637" style={{ width: "100%" }} />
            {uniprotValid === "checking" && <p style={{ fontSize: "11px", color: "#6b7280", margin: "3px 0 0" }}>Checking…</p>}
            {uniprotValid === true  && <p style={{ fontSize: "11px", color: "#16a34a", margin: "3px 0 0" }}>Valid UniProt ID</p>}
            {uniprotValid === false && <p style={{ fontSize: "11px", color: "#dc2626", margin: "3px 0 0" }}>Invalid UniProt ID</p>}
          </div>

          <label>Immunogen</label>
          <textarea name="immunogen" value={formData.immunogen}
            onChange={(e) => { handleChange(e); handleAutoResize(e); }}
            placeholder="e.g., Recombinant full-length human p53"
            rows={1} style={{ resize: "none", overflow: "hidden", minHeight: "38px", lineHeight: "1.5" }} />

          <label>Immunogen Sequence</label>
          <textarea name="immunogen_sequence" value={formData.immunogen_sequence}
            onChange={(e) => { handleChange(e); handleAutoResize(e); }}
            placeholder="Amino acid or nucleotide sequence…"
            rows={1} style={{ resize: "none", overflow: "hidden", minHeight: "38px", lineHeight: "1.5", width: "100%" }} />

          <label>Epitope Mapped</label>
          <select name="epitope_mapped" value={formData.epitope_mapped} onChange={handleChange}>
            <option value="">Select</option>
            <option>Yes</option>
            <option>No</option>
          </select>
          {formData.epitope_mapped === "Yes" && (
            <>
              <label>Epitope Location / Sequence</label>
              <textarea name="epitope_location_sequence" value={formData.epitope_location_sequence}
                onChange={(e) => { handleChange(e); handleAutoResize(e); }}
                placeholder="e.g., C-terminal residues 350–393"
                rows={1} style={{ resize: "none", overflow: "hidden", minHeight: "38px", lineHeight: "1.5", width: "100%" }} />
            </>
          )}

          <PubmedIdField
            label="Initial Publication PubMed ID(s)" name="initial_pubmed_id"
            value={formData.initial_pubmed_id} onChange={handleChange}
            onBlur={() => validateIds("initial_pubmed_id", formData.initial_pubmed_id)}
            validity={validityMap["initial_pubmed_id"] || {}}
          />

          <div className="full-width-field">
            <label>Depositor Notes</label>
            <p className="field-hint"><span className="strongly-encouraged">Encouraged: </span>
              Dilution recommendations, storage tips, characterization details.</p>
            <textarea name="depositor_notes" value={formData.depositor_notes}
              onChange={handleChange} placeholder="e.g., 1:1000 for WB, store at -20°C…"
              style={{ minHeight: "90px" }} />
          </div>

          <div className="full-width-field">
            <label>Additional Information</label>
            <textarea name="additional_information" value={formData.additional_information}
              onChange={handleChange} placeholder="Any additional context about this antibody…"
              style={{ minHeight: "90px" }} />
          </div>

          <div className="protocol-upload-section">
            <h3>Protocol / Reference File <span style={{ fontWeight: 400, color: "#9ca3af" }}>(Optional)</span></h3>
            <p>PDF or Word document — protocols, characterization data</p>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <button type="button" className="text-link" onClick={() => protocolFileInputRef.current?.click()}>
                {formData.protocol_file ? formData.protocol_file.name : (formData.protocol_file_name || "Choose file")}
              </button>
              <input ref={protocolFileInputRef} type="file" accept=".pdf,.doc,.docx"
                style={{ display: "none" }} onChange={handleProtocolFileChange} />
              {(formData.protocol_file || formData.protocol_file_name) && (
                <button type="button"
                  onClick={() => setFormData(p => ({
                    ...p, protocol_file: null, protocol_file_name: null,
                    file_temp_key: null, protocol_file_preview_url: null,
                  }))}
                  style={{ color: "#dc2626", fontSize: "12px", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
                  Remove
                </button>
              )}
            </div>
          </div>

          <label style={{ gridColumn: "span 2" }}>Recommended Applications</label>
          <div className="checkbox-group">
            {applicationsList.map(app => (
              <label key={app} style={{ display: "inline-flex", gap: "6px", marginRight: "12px", marginBottom: "6px", fontSize: "13px" }}>
                <input type="checkbox" value={app}
                  checked={formData.applications.includes(app)}
                  onChange={handleCheckboxChange} />
                {app}
              </label>
            ))}
          </div>

          {formData.applications.map(app => {
            const f = applicationToPubmedMap[app];
            return f ? (
              <PubmedIdField key={f} label={pubmedLabels[f]} name={f}
                value={formData[f] ?? ""} onChange={handleChange}
                onBlur={() => validateIds(f, formData[f] ?? "")}
                validity={validityMap[f] || {}} />
            ) : null;
          })}

          <div className="form-buttons" style={{ gridColumn: "span 2", marginTop: "8px", gap: "8px" }}>
            {/* ── Simplified to Save / Cancel — "Save" reuses saveAndAddAnother.
                 Old buttons kept below (commented out) rather than deleted. ── */}
            {/*
            <button type="button" onClick={saveDraft} disabled={fileUploading || submitting}
              style={{ padding: "10px 22px", background: "#fff", color: "#1a2c5b", border: "1.5px solid #1a2c5b", borderRadius: "6px", fontSize: "13px", fontWeight: 600, cursor: fileUploading ? "not-allowed" : "pointer", opacity: fileUploading ? 0.7 : 1 }}>
              {editingEntryId !== null ? "Update Draft" : "Save Draft"}
            </button>
            */}
            <button type="button" onClick={saveAndAddAnother} disabled={fileUploading || submitting}
              style={{ padding: "10px 22px", background: "#1a2c5b", color: "#fff", border: "none", borderRadius: "6px", fontSize: "13px", fontWeight: 600, cursor: fileUploading ? "not-allowed" : "pointer", opacity: fileUploading ? 0.7 : 1 }}>
              {fileUploading ? "Uploading…" : "Save"}
            </button>
            {/*
            <button type="button"
              onClick={() => {
                if (!validateForm()) { toast.error("Please fill all required fields."); return; }
                const currentEntry = { ...buildEntry(), id: editingEntryId || Date.now() };
                const allToSubmit = [...draftEntries.filter(e => e.id !== editingEntryId), currentEntry];
                const incomplete = allToSubmit.filter(e =>
                  mandatoryFields.some(f => !e[f] || !String(e[f]).trim())
                );
                if (incomplete.length > 0) {
                  const names = incomplete.map(e => e.item_name_number || "(unnamed)").join(", ");
                  toast.error(
                    `${incomplete.length} entry(ies) missing required fields: ${names}. Edit and complete them before submitting.`,
                    { autoClose: 8000 }
                  );
                  return;
                }
                doSubmit(allToSubmit);
              }}
              disabled={fileUploading || submitting}
              style={{ padding: "10px 22px", background: "#15803d", color: "#fff", border: "none", borderRadius: "6px", fontSize: "13px", fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.7 : 1 }}>
              {submitting ? "Submitting…" : "Save & Submit All"}
            </button>
            */}
            <button type="button" onClick={requestCancel} disabled={submitting}
              style={{ padding: "10px 18px", background: "none", color: "#6b7280", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px", cursor: "pointer", fontWeight: 500 }}>
              Cancel
            </button>
          </div>
        </form>
      </div>

      {showCancelConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: "10px", padding: "24px", width: "360px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <h3 style={{ margin: "0 0 8px", fontSize: "15px", color: "#1a2c5b" }}>Discard changes?</h3>
            <p style={{ margin: "0 0 18px", fontSize: "13px", color: "#6b7280" }}>
              {editingEntryId !== null
                ? "Any unsaved changes to this draft will be lost. The draft itself will remain as it was."
                : "The information you've entered in this form will be lost."}
            </p>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => setShowCancelConfirm(false)} style={{ background: "none", border: "1px solid #d1d5db", borderRadius: "5px", padding: "7px 14px", cursor: "pointer", fontSize: "12px", color: "#6b7280" }}>No, keep editing</button>
              <button onClick={confirmCancel} style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: "5px", padding: "7px 16px", cursor: "pointer", fontSize: "12px", fontWeight: 600 }}>Yes, discard</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const VALID_FIELD_KEYS = new Set([
  "item_name_number","isotype","antigen","alternative_antigen_names",
  "depositor_growth_medium","depositor_institution","depositor_email",
  "depositor_website","depositor_name_contributor","host_species",
  "antigen_species","positive_tested_species","alternate_name_clone_id",
  "additional_information","additional_characterization","initial_pubmed_id",
  "depositor_notes","antigen_molecular_weight","gene","alternate_gene_names",
  "entrez_gene_id","uniprot_id","immunogen","immunogen_sequence","clonality",
  "myeloma_strain","epitope_mapped","epitope_location_sequence",
  "epitope_map_pubmed_id","ihc_pubmed_id","if_pubmed_ids","wb_pubmed_id",
  "ip_pubmed_ids","chip_pubmed_ids","elisa_pubmed_ids","ffpe_pubmed_ids",
  "fb_pubmed_ids","microarray_pubmed_ids","trap_pubmed_ids","facs_pubmed_ids",
  "gel_supershift_pubmed_ids","other_pubmed_ids","applications","collections",
]);

function mapExcelRow(row) {
  const MAP = {
    "item name/number *":"item_name_number","item name/number":"item_name_number",
    "item name*":"item_name_number","item name":"item_name_number",
    "isotype *":"isotype","isotype":"isotype","antigen":"antigen",
    "alternative antigen names":"alternative_antigen_names","uniprot id":"uniprot_id",
    "depositor email *":"depositor_email","depositor email":"depositor_email",
    "depositor(s) email":"depositor_email","depositor(s) email *":"depositor_email",
    "depositor email address":"depositor_email",
    "email":"depositor_email","email *":"depositor_email",
    "email address":"depositor_email","depositor e-mail":"depositor_email",
    "depositor name/contributor *":"depositor_name_contributor",
    "depositor name/contributor":"depositor_name_contributor",
    "depositor name*":"depositor_name_contributor","depositor name":"depositor_name_contributor",
    "depositor institution":"depositor_institution","depositor website":"depositor_website",
    "depositor(s) institution":"depositor_institution","depositor(s) website":"depositor_website",
    "depositor growth medium":"depositor_growth_medium","host species":"host_species",
    "antigen species":"antigen_species","positive tested species":"positive_tested_species",
    "antigen molecular weight":"antigen_molecular_weight","gene":"gene",
    "alternate gene names":"alternate_gene_names","entrez gene id":"entrez_gene_id",
    "clonality":"clonality","myeloma strain":"myeloma_strain",
    "alternate name / clone id":"alternate_name_clone_id",
    "alternate name/clone id":"alternate_name_clone_id",
    "epitope mapped":"epitope_mapped","epitope location/sequence":"epitope_location_sequence",
    "immunogen":"immunogen","immunogen sequence":"immunogen_sequence",
    "additional information":"additional_information",
    "additional characterization":"additional_characterization",
    "depositor notes":"depositor_notes",
    "initial publication pubmed id":"initial_pubmed_id","initial pubmed id":"initial_pubmed_id",
    "epitope map pubmed id":"epitope_map_pubmed_id",
    // NetSuite export column headers (from generated CSV) — new/unique ones only
    "depositor notes (special instructions)":"depositor_notes",
    "positive tested species reactivity":"positive_tested_species",
    "alternate gene name(s)":"alternate_gene_names",
    "epitope location or sequence":"epitope_location_sequence",
    "additional characterization:":"additional_characterization",
    "immunohistochemistry pubmed id":"ihc_pubmed_id",
    "immunofluorescence pubmed ids":"if_pubmed_ids",
    "western blot pubmed id":"wb_pubmed_id",
    "immunoprecipitation pubmed ids":"ip_pubmed_ids",
    "chromatin immunoprecipitation pubmed ids":"chip_pubmed_ids",
    "function blocking pubmed ids":"fb_pubmed_ids",
    "pubmed ids":"other_pubmed_ids",
    "recommended applications":"applications","applications":"applications",
    "collections":"collections","collection":"collections",
    "ihc pubmed id":"ihc_pubmed_id","if pubmed ids":"if_pubmed_ids",
    "wb pubmed id":"wb_pubmed_id","ip pubmed ids":"ip_pubmed_ids",
    "chip pubmed ids":"chip_pubmed_ids","elisa pubmed ids":"elisa_pubmed_ids",
    "ffpe pubmed ids":"ffpe_pubmed_ids","fb pubmed ids":"fb_pubmed_ids",
    "microarray pubmed ids":"microarray_pubmed_ids","trap pubmed ids":"trap_pubmed_ids",
    "facs pubmed ids":"facs_pubmed_ids","gel supershift pubmed ids":"gel_supershift_pubmed_ids",
    "other pubmed ids":"other_pubmed_ids",
  };
  const mapped = {};
  Object.keys(row).forEach(key => {
    const trimmedKey = key.toLowerCase().trim();
    const rawKeyCandidate = key.trim().toLowerCase().replace(/\s+/g, "_");
    const field = MAP[trimmedKey] || (VALID_FIELD_KEYS.has(rawKeyCandidate) ? rawKeyCandidate : null);
    if (!field) return;
    let val = row[key];
    if (val === null || val === undefined || String(val).trim() === "") return;
    val = String(val).trim();
    if (field === "clonality")
      val = val.toLowerCase().includes("poly") ? "Polyclonal" : "Monoclonal";
    if (field === "applications")
      // NetSuite exports use | as separator; depositors may use comma
      val = val.split(/[|,]/).map(a => a.replace(/[☑☐✓✔]/g, "").trim()).filter(Boolean);
    if (field === "collections")
      // Same as applications - the generated CSV export joins with "|",
      // and CollectionsMultiSelect (the live form's own picker) stores
      // this as an array, not a plain string, so an imported row needs
      // the same split.
      val = val.split(/[|,]/).map(c => c.trim()).filter(Boolean);
    if (titleCaseFields.has(field)) val = toTitleCase(val);
    mapped[field] = val;
  });
  return mapped;
}   